import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  CreatePaymentRequest,
  OrderPayment,
  PaymentListRow,
  RestaurantPaymentsExportQuery,
  RestaurantPaymentsQuery,
} from "./pos.type";
import { ReceiptApi } from "./receipt.api";
import {
  ReportDownload,
  parseContentDispositionFilename,
} from "@/lib/reportDownload";

export const PaymentApi = createApi({
  reducerPath: "paymentApi",
  tagTypes: ["payment"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    addPayment: build.mutation<
      OrderPayment,
      { orderUuid: string; data: CreatePaymentRequest }
    >({
      query: ({ orderUuid, data }) => ({
        url: `orders/${orderUuid}/payments`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["payment"],
      // The receipt lives in a separate RTK Query slice, so `invalidatesTags`
      // can't reach it. Recording a payment changes the receipt's paid status
      // (remainingAmount), so invalidate it cross-slice — otherwise a receipt
      // fetched before payment (e.g. the "Addition" preview) stays cached and
      // the preview keeps showing "Non payé" for an order that is now paid.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ReceiptApi.util.invalidateTags(["receipt"]));
        } catch {
          // Payment failed — nothing changed, leave the receipt cache as is.
        }
      },
    }),

    getOrderPayments: build.query<
      PaginationResults<OrderPayment>,
      { orderUuid: string }
    >({
      query: ({ orderUuid }) => ({
        url: `orders/${orderUuid}/payments`,
        method: "GET",
      }),
      providesTags: ["payment"],
      transformResponse: (data: CollectionData<OrderPayment>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getPayment: build.query<OrderPayment, { paymentUuid: string }>({
      query: ({ paymentUuid }) => `order_payments/${paymentUuid}`,
      providesTags: ["payment"],
    }),

    // Reports drill-down: individual payments for the current restaurant
    // (scoped by the X-Restaurant-Id header). See SFAPI-94.
    getRestaurantPayments: build.query<
      PaginationResults<PaymentListRow>,
      RestaurantPaymentsQuery
    >({
      query: (params) => ({
        url: `payments`,
        method: "GET",
        params,
      }),
      providesTags: ["payment"],
      transformResponse: (data: CollectionData<PaymentListRow>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    // CSV ledger export of the same restaurant payments collection. Honours the
    // same filters as the list (method, status, date range, q) and returns a
    // file blob plus the server-provided filename. See SFAPI-94 /payments/export.
    getRestaurantPaymentsCsv: build.query<
      ReportDownload,
      RestaurantPaymentsExportQuery
    >({
      query: (params) => ({
        url: `payments/export`,
        method: "GET",
        params,
        responseHandler: async (response: Response) => ({
          blob: await response.blob(),
          filename: parseContentDispositionFilename(response),
        }),
      }),
    }),
  }),
});

export const {
  useAddPaymentMutation,
  useGetOrderPaymentsQuery,
  useGetPaymentQuery,
  useGetRestaurantPaymentsQuery,
  useLazyGetRestaurantPaymentsCsvQuery,
} = PaymentApi;
